/** @jsx jsx */
import { jsx } from '@emotion/react';
import { AfterPage, AfterPageProps } from '@sprinx/after-razzle';
import contactData from 'api/contactData';
import Breadcrumbs from 'components/Breadcrumbs';
import Certifications from 'components/Certifications';
import Container from 'components/Container';
import Page from 'components/Page';
import MainLayout from 'layouts/MainLayout';
import ErrorPage from 'pages/ErrorPage';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import LocalizationProvider from 'providers/LocalizationProvider';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { routeUrl } from 'routesBuilder';
import { ContactData } from 'types/ContactData';
import { SupportedLocale, SupportedRegion } from 'types/Supported';
import ContactCard from './components/ContactCard';
import ContactInfoContactForm from './components/ContactInfoContactForm';
import ContactPageInfo from './components/ContactPageInfo';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ContactPageProps extends AfterPageProps<ContactPageRouteParams> {
  data?: ContactData;
  locale: SupportedLocale;
  region: SupportedRegion;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ContactPageRouteParams {
  locale: SupportedLocale;
  region: SupportedRegion;
}

const ContactPage: AfterPage<ContactPageRouteParams, ContactPageProps> = ({ data, error, locale, region }) => {
  if (error) return <ErrorPage locale={locale} region={region} error={error} />;
  if (!data) return <NotFoundPage locale={locale} region={region} />;
  const { layout, contacts = [], certifications = [] } = data;
  const regionInfo = contacts.find((i) => i.region === region);
  const contactCards = contacts.filter((i) => i.region !== region);
  const colors = ['#a0bc4d', '#85b752', '#509c51', '#327242'];
  const countries = contactCards.map((i, index) => ({
    color: colors[index],
    region: i.region,
  }));

  return (
    <LocalizationProvider locale={locale} region={region}>
      <MainLayout {...layout} locale={locale}>
        <Page
          breadcrumbs={
            <Breadcrumbs>
              <Link to={routeUrl('home', { locale, params: { locale: locale, region: region } })}>AppOn.cloud</Link>
              <Link to={routeUrl('contact', { locale, params: { locale: locale, region: region } })} className='active'>
                <FormattedMessage id={'navigation.contacts'} />
              </Link>
            </Breadcrumbs>
          }
          pageTitle={data.title}
          css={{ width: '100%' }}
        >
          {regionInfo && <ContactPageInfo contact={regionInfo} mapValues={countries} />}
          <div
            css={(theme) => ({
              alignItems: 'center',
              background: theme.palette.backgroundGradient,
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              width: '100%',
            })}
          >
            <Container maxWidth='lg' css={{ flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
              {contactCards
                .filter((i) => i.region && ['cesko', 'slovensko', 'madarsko'].includes(i.region))
                .map((contact, idx) => (
                  <ContactCard key={idx.toString()} contact={contact} />
                ))}
            </Container>
            <ContactInfoContactForm />
          </div>
          <Certifications certifications={certifications} />
        </Page>
      </MainLayout>
    </LocalizationProvider>
  );
};

ContactPage.getInitialProps = async ({ match }) => {
  const { locale, region } = match.params;
  const data = await contactData.get({ locale, region });

  return {
    data,
    locale,
    region,
  };
};

export default ContactPage;
